<script setup lang="ts">
import { useJsonLdSchema } from '~/composables/useJsonLd';
import { Button } from '~/components/ui/button'
import { Skeleton } from '~/components/ui/skeleton'
import FiltersModal from '~/components/custom-ui/FiltersModal.vue'
import CategoriesModal from '~/components/custom-ui/CategoriesModal.vue'
import BtnFilterLabels from '~/components/custom-ui/filterFields/BtnFilterLabels.vue'
import BtnFilterRangeInline from '~/components/custom-ui/filterFields/BtnFilterRangeInline.vue'
import BtnFilterCheckbox from '~/components/custom-ui/filterFields/BtnFilterCheckbox.vue'
import BtnFilterRange from '~/components/custom-ui/filterFields/BtnFilterRange.vue'
import MasterCard from '~/components/pages/catalog/MasterCard.vue'
import BtnFilterSelect from '~/components/custom-ui/filterFields/BtnFilterSelect.vue'
import BtnFilterLabelSingle from '~/components/custom-ui/filterFields/BtnFilterLabelSingle.vue'
import Pagination from "~/components/custom-ui/Pagination.vue";
import MiniMap from '~/components/pages/catalog/MiniMap.vue'
import BtnFilterBoolean from '~/components/custom-ui/filterFields/BtnFilterBoolean.vue'
import { useMediaQuery } from '@vueuse/core'

const route = useRoute();
const config = useRuntimeConfig()

const globalStore = useGlobalStore();
const userStore = useUserStore();

const specificationId = computed(() => {
    if(globalStore.specifications.find(item => item.documentId === route.params.slug)){
        return route.params.slug
    }
    
    const foundSpecification = globalStore.specifications.find(item => item.slug === route.params.slug)?.documentId
    if (!foundSpecification) {
        throw createError({
            statusCode: 404,
            message: 'Specification not found'
        })
    }
    return foundSpecification
})

const {
    isLoaded,
    filterForm,
    mastersList,
    total,
    filtersList,
    currentSpecification,
    parentSpecification,
    specificationChildList,
    cleanedFilterForm,
    getFilters,
    getMasters
} = usePortfolio(specificationId.value as string);

await getFilters()
await getMasters()

const appliedFilters = ref<typeof filterForm.value>(JSON.parse(JSON.stringify(filterForm.value)))

const router = useRouter();

// Синхронизация страницы с URL (для пагинации)
watch(() => filterForm.value.page, (page) => {
    router.push({
        query: {
            ...route.query,
            page: page
        },
        params: {
            ...route.params
        }
    });
});

// Функция для синхронизации примененных фильтров с URL
function syncAppliedFiltersToUrl() {
    const filters = appliedFilters.value;
    const query: Record<string, any> = { ...route.query };
    
    // Обновляем или удаляем параметры фильтров
    if (filters.city && filters.city.length > 0) {
        query.city = filters.city.join('|');
    } else {
        delete query.city;
    }
    
    if (filters.experienceFrom !== null && filters.experienceFrom !== undefined) {
        query.experienceFrom = filters.experienceFrom;
    } else {
        delete query.experienceFrom;
    }
    
    if (filters.educationType && filters.educationType.length > 0) {
        query.educationType = filters.educationType.join('|');
    } else {
        delete query.educationType;
    }
    
    if (filters.employmentType && filters.employmentType.length > 0) {
        query.employmentType = filters.employmentType.join('|');
    } else {
        delete query.employmentType;
    }
    
    if (filters.priceFrom !== null && filters.priceFrom !== undefined) {
        query.priceFrom = filters.priceFrom;
    } else {
        delete query.priceFrom;
    }
    
    if (filters.priceTo !== null && filters.priceTo !== undefined) {
        query.priceTo = filters.priceTo;
    } else {
        delete query.priceTo;
    }
    
    // Страница будет обновлена через watch на filterForm.value.page
    // Но мы все равно устанавливаем её здесь для консистентности
    query.page = appliedFilters.value.page || 1;
    
    router.push({
        query,
        params: {
            ...route.params
        }
    });
}

const currentCity = computed(() => {
    return globalStore.locations.cities?.find(item => item.name.toLowerCase().replace(/ /g, '-') === route.params.city)
})

const citySeo = computed(() => {
    const city = currentSpecification.value?.seos?.find(item => item.city.name.toLowerCase().replace(/ /g, '-') === route.params.city) || currentSpecification.value?.seos?.find(item => item.city.name.toLowerCase().replace(/ /g, '-') === 'dubai')
    if(city) return city
    return {
        title: seoTitle,
        description: seoDescription,
        page_title: null,
        page_description: null,
    }
})

const seoTitle = computed(() => {
    if(currentSpecification.value?.title && currentCity.value?.name && currentSpecification.value?.title.includes('{city}')){
        return currentSpecification.value?.title.replace('{city}', currentCity.value?.name)
    }
    return currentSpecification.value?.name
})

const seoDescription = computed(() => {
    if(currentSpecification.value?.description){
        if(currentCity.value?.name && currentSpecification.value?.description.includes('{city}')){
            return currentSpecification.value?.description.replace('{city}', currentCity.value?.name)
        }
        return currentSpecification.value?.description
    }
    return currentSpecification.value?.description
})

useSeoMeta({
    robots: 'index, follow',
    title: citySeo.value?.title,
    description: citySeo.value?.description,
    ogImage: currentSpecification.value?.thumbnail?.url,
    ogImageType: 'image/jpeg',
    ogType: 'website',
    ogUrl: () => config.public.frontUrl + route.path,
    ogTitle: citySeo.value?.title,
    ogDescription: citySeo.value?.description,
    twitterTitle: citySeo.value?.title,
    twitterDescription: citySeo.value?.description,
    twitterImage: currentSpecification.value?.thumbnail?.url,
    twitterImageType: 'image/jpeg',
})

useHead({
    link: [
        {
            rel: 'canonical',
            href: `${config.public.frontUrl}${currentSpecification.value?.slug ? '/catalog/category/' + route.params.city + '/' + currentSpecification.value.slug : route.path}`,
        },
    ],
})

const { jsonLd } = useJsonLdSchema({
    breadcrumbs: computed(() => {
        const items = [];

        if (parentSpecification.value) {
            items.push({
                name: parentSpecification.value.name,
                url: `${config.public.frontUrl}/catalog/category/${parentSpecification.value.slug}`
            });
        }

        if (currentSpecification.value) {
            items.push({
                name: currentSpecification.value.name,
                url: `${config.public.frontUrl}/catalog/category/${currentSpecification.value.slug}`
            });
        }

        return items;
    }),
    items: computed(() => mastersList.value?.map(master => ({
        name: master.user.username || '',
        url: `${config.public.frontUrl}/catalog/portfolio/${master.documentId}`,
        description: currentSpecification.value?.description || '',
        image: master.user.avatar?.url || '',
        jobTitle: currentSpecification.value?.name || ''
    })) || []),
    pageData: computed(() => ({
        name: currentSpecification.value?.name || 'Master\'s catalog',
        description: currentSpecification.value?.description || 'Browse catalog of professionals',
        url: `${config.public.frontUrl}${route.path}`
    })),
    homeUrl: config.public.frontUrl
});

useHead({
  script: [
    {
      type: 'application/ld+json',
      id: 'custom-jsonld-schema',
      children: JSON.stringify(jsonLd.value)
    }
  ]
})

// Исправляем типы для фильтров
const reviewsOptions = computed(() => filtersList.value?.reviews?.map(review => ({
    name: review.label,
    documentId: review.value.toString(),
    value: review.value
})) || []);

const { parseMarkdown } = useMarkdown()
const citySeoContent = computed(() => {
    if(citySeo.value?.page_description && typeof citySeo.value.page_description === 'string') {
        return parseMarkdown(citySeo.value.page_description)
    }
    return null
})

const hasActiveFilters = computed(() => {
    const form = appliedFilters.value
    return !!(
        (form.priceFrom !== null && form.priceFrom !== undefined) ||
        (form.priceTo !== null && form.priceTo !== undefined) ||
        (form.employmentType && form.employmentType.length > 0) ||
        (form.educationType && form.educationType.length > 0) ||
        (form.experienceFrom !== null && form.experienceFrom !== undefined) ||
        form.isAvailableToday === true ||
        form.withRatingOverFour === true
    )
})

function clearFilters() {
    const currentCity = filterForm.value.city
    filterForm.value.priceFrom = null
    filterForm.value.priceTo = null
    filterForm.value.employmentType = []
    filterForm.value.educationType = []
    filterForm.value.experienceFrom = null
    filterForm.value.isAvailableToday = false
    filterForm.value.withRatingOverFour = false
    filterForm.value.page = 1
    filterForm.value.city = currentCity
    applyFilters()
}

async function applyFilters() {
    filterForm.value.page = 1
    appliedFilters.value = JSON.parse(JSON.stringify(filterForm.value))
    syncAppliedFiltersToUrl()
    await getMasters()
}

const isMobile = computed(() => {
    return useMediaQuery('(max-width: 1024px)')
})

</script>

<template>
    <div class="grid gap-12 lg:grid-cols-4">
        <div class="aside hidden lg:block">
            <div class="page_title">
                <div class="breadcrumb mb-4">
                    <NuxtLink 
                        to="/"
                        class="group text-md text-gray-400 hover:text-gray-600 transition-all flex items-center">
                        {{ $t('Home') }}
                        <Icon name="ri:arrow-drop-right-line" size="24px" class="ml-0 transition-all group-hover:ml-1 rtl:rotate-180" />
                    </NuxtLink>
                </div>
                <h1 class="text-3xl font-bold mb-7" v-if="!isMobile.value">{{ citySeo?.page_title || currentSpecification?.name }}</h1>
            </div>
            <ul class="nav">
                <li v-if="parentSpecification" class="my-1">
                    <NuxtLink 
                        :to="{name: 'master_category', params: {slug: parentSpecification.slug, city: userStore.currentCity?.slug}}"
                        class="list-container block w-full py-2 px-3 transition-all hover:pl-4 rtl:hover:pr-4"
                        :class="{ list_container_active: parentSpecification.documentId == filterForm.specificationId[0] }">
                        {{ parentSpecification.name }}
                    </NuxtLink>
                </li>
                <li class="my-1 pl-4" v-for="item in specificationChildList">
                    <NuxtLink 
                        :to="{name: 'master_category', params: {slug: item.slug, city: userStore.currentCity?.slug}}"
                        class="list-container block w-full py-2 px-3 transition-all hover:pl-4 rtl:hover:pr-4"
                        :class="{ list_container_active: item.documentId == filterForm.specificationId[0] }">
                        {{ item.name }}
                    </NuxtLink>
                </li>
            </ul>
            
        </div>

        <div class="col-span-1 lg:col-span-3">
            <div class="page_title lg:hidden mb-4">
                <div class="breadcrumb mb-2">
                    <NuxtLink 
                        to="/"
                        class="group text-sm text-gray-400 hover:text-gray-600 transition-all flex items-center">
                        {{ $t('Home') }}
                        <Icon name="ri:arrow-drop-right-line" size="20px" class="ml-0 transition-all group-hover:ml-1 rtl:rotate-180" />
                    </NuxtLink>
                </div>
                <h1 class="text-2xl font-bold" v-if="isMobile.value">{{ citySeo?.page_title || currentSpecification?.name }}</h1>
            </div>
            <client-only>
                <MiniMap :filters="cleanedFilterForm" :specification-id="filterForm.specificationId[0]" class="mb-6" />
            </client-only>
            <div class="flex flex-wrap gap-4 mb-6">
                <FiltersModal @apply="applyFilters">
                    <template #trigger>
                        <Button 
                            variant="lighter" 
                            class="text-main gap-2 flex items-center justify-between"
                            :class="{
                                'bg-primary-500 text-white hover:bg-primary-600 hover:text-white': hasActiveFilters
                            }"
                        >
                            <div class="flex items-center gap-1">
                                <Icon size="15" class="mr-1" name="ri:filter-3-line"/>
                                <span class="text-sm">{{ $t("Filters") }}</span>
                            </div>
                            <div v-if="hasActiveFilters" class="w-4 h-4 rounded-full flex items-center justify-center shrink-0 transition-colors bg-white" @click.prevent.stop="clearFilters">
                                <Icon name="ri:close-fill" class="text-gray-500" size="12" />
                            </div>
                        </Button>
                    </template>
                    <div class="px-5 grid grid-cols-1 gap-6">
                        <BtnFilterLabels
                            v-if="filtersList?.cities?.length"
                            v-model="filterForm.city"
                            :label="$t('Location')"
                            :options="filtersList?.cities"
                        />
                        <BtnFilterRangeInline
                            v-if="filtersList?.price && filtersList?.price?.from !== null && filtersList?.price?.to"
                            v-model:minValue="filterForm.priceFrom"
                            v-model:maxValue="filterForm.priceTo"
                            :min="filtersList.price.from"
                            :max="filtersList.price.to"
                            :label="$t('Price range')"
                        />
                        <!-- <CustomUiBtnFilterLabelSingle
                            v-if="filtersList?.reviews?.length"
                            v-model="filterForm.reviewsCount"
                            :label="$t('Reviews')"
                            :options="reviewsOptions"
                            option-label="name"
                            option-value="value"
                            class="mb-8"
                        /> -->
                        
                        <BtnFilterLabels
                            v-if="filtersList?.employments?.length"
                            v-model="filterForm.employmentType"
                            :label="$t('Employment')"
                            :options="filtersList.employments"
                        />
                        <BtnFilterLabels
                            v-if="filtersList?.educations?.length"
                            v-model="filterForm.educationType"
                            :label="$t('Education')"
                            :options="filtersList.educations"
                        />
                        <BtnFilterLabelSingle
                            v-if="filtersList?.experiences?.length"
                            v-model="filterForm.experienceFrom"
                            :label="$t('Experience')"
                            :options="filtersList.experiences"
                            option-label="label"
                            option-value="value"
                            icon="ri:briefcase-2-fill"
                            btn_class="max-h-[36px] px-2 w-full"
                            class="" 
                        />
                        <div class="grid grid-cols-1 gap-2">
                            <BtnFilterBoolean
                                v-model="filterForm.isAvailableToday"
                                :label="$t('Available today')"
                            />
                            <BtnFilterBoolean
                                v-model="filterForm.withRatingOverFour"
                            >
                                <template v-slot="{ checked }">
                                    <div class="flex items-center gap-1">
                                        <span>
                                            {{ $t('Rated 4 and up') }}
                                        </span>
                                        <Icon name="ri:star-fill" size="14" :class="{
                                            'text-amber-500': !checked,
                                            'text-white-500': checked
                                        }" />
                                    </div>
                                </template>
                            </BtnFilterBoolean>
                        </div>
                    </div>
                </FiltersModal>
                <BtnFilterCheckbox
                    v-if="filtersList?.cities?.length"
                    v-model="filterForm.city"
                    :label="$t('Location')"
                    :options="filtersList?.cities"
                    icon="ri:user-location-line"
                    btn_class="whitespace-nowrap rounded-md text-sm font-medium ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-stroke hover:bg-transparent border border-transparent hover:border-gray-200 hover:text-main h-10 px-4 py-2 text-main gap-1 flex items-center justify-between"
                    class="" />
                <CategoriesModal v-if="!route.path.includes('search')" type="masters" >
                    <template #trigger>
                        <Button variant="lighter" class="text-black md:hidden">
                            <Icon size="15" class="mr-1" name="ri:menu-search-line"/>
                            <span class="text-sm">{{ currentSpecification?.name ? (currentSpecification.name.length > 25 ? currentSpecification.name.slice(0, 25) + '...' : currentSpecification.name) : $t("Category") }}</span>
                        </Button>
                    </template>
                </CategoriesModal>
            </div>
            <!-- <div v-if="filtersList" class="category_filters hidden md:flex flex-wrap gap-2 mb-8">
                <BtnFilterCheckbox
                    v-if="filtersList?.cities?.length"
                    v-model="filterForm.city"
                    :label="$t('Location')"
                    :options="filtersList?.cities"
                    icon="ri:user-location-line"
                    btn_class="max-h-[36px] px-2 w-full"
                    class="" />
                <BtnFilterRange
                    v-if="filtersList?.price && filtersList?.price?.from !== null && filtersList?.price?.to"
                    v-model:minValue="filterForm.priceFrom"
                    v-model:maxValue="filterForm.priceTo"
                    :min="filtersList.price.from"
                    :max="filtersList.price.to"
                    :label="$t('Price range')"
                    icon="ri:coins-line"
                    btn_class="max-h-[36px] px-2 w-full"
                    class="col-span-1" />
                <BtnFilterCheckbox
                    v-if="filtersList?.employments?.length"
                    v-model="filterForm.employmentType"
                    :label="$t('Employment')"
                    :options="filtersList.employments"
                    icon="ri:briefcase-2-fill"
                    btn_class="max-h-[36px]
                    px-2 w-full"
                    class="" />
                <BtnFilterCheckbox
                    v-if="filtersList?.educations?.length"
                    v-model="filterForm.educationType"
                    :label="$t('Education')"
                    :options="filtersList.educations"
                    icon="ri:book-line"
                    btn_class="max-h-[36px] px-2 w-full"
                    class="" />
                <BtnFilterSelect
                    v-if="filtersList?.experiences?.length"
                    v-model="filterForm.experienceFrom"
                    :label="$t('Experience')"
                    :options="filtersList.experiences"
                    icon="ri:briefcase-2-fill"
                    btn_class="max-h-[36px] px-2 w-full"
                    class="" />
            </div> -->
            <template v-if="isLoaded">
                <div v-if="mastersList?.length" class="grid grid-cols-1 gap-8" >
                    <MasterCard v-for="item in mastersList" :item="item" :current_specification="filterForm.specificationId[0]" :parent_specification="parentSpecification?.documentId" />
                    <Pagination v-if="mastersList.length < total" @update:page="(page) => filterForm.page = page" :total="total" :per_page="filterForm.pageSize" :page="filterForm.page" class="mx-auto" />
                    <!-- <PaginationBox v-if="mastersList.length < total" @update:page="(page) => filterForm.page = page" :total="total" :per_page="filterForm.pageSize" :page="filterForm.page" class="mx-auto" /> -->

                    
                </div>

                <div v-else class="flex flex-col items-center py-10" >
                    <img src="/img/EmptyStates.png" class="mb-4">
                    <p class="ys-home-custom-text text-center mb-8">
                        {{ $t("There are no specialists in this category yet") }}
                    </p>
                    <Button variant="outline" v-if="userStore.isAuthenticated" as-child class="text-md py-6 px-6 border-primary-500 rounded-lg font-medium text-primary-500 hover:bg-primary-50 hover:text-primary-500 hover:border-transparent">
                        <NuxtLink to="/office/profile/">
                            <Icon size="20" class="mr-1" name="ri:user-line" />
                            {{ $t("Become a specialist") }}
                        </NuxtLink>
                    </Button>
                </div>

                <div v-if="citySeoContent || (currentSpecification && currentSpecification.seos.length)" class="mt-6">
                    <div v-if="citySeoContent" class="page_content" v-html="citySeoContent" />

                    <div v-if="currentSpecification && currentSpecification.seos.length" class="flex gap-4">
                        <Button v-for="item in currentSpecification?.seos" variant="outline" size="xs" class="text-xs" as-child>
                            <NuxtLink :to="{name: 'master_category', params: {city: item.city.name.toLowerCase().replace(/ /g, '-'), slug: currentSpecification.slug}}">
                                {{ item.city.name }}
                            </NuxtLink>
                        </Button>
                    </div>
                </div>
            </template>
            <template v-else>
                <div class="grid grid-cols-1 gap-8">
                    <Skeleton class="rounded-xl h-60" v-for="i in 5" />
                </div>
            </template>
        </div>
    </div>
</template>

<style scoped lang="scss">
.list-container {
    transition: padding 0.5s ease;
    color: rgba(10, 13, 20, 1);

    &:hover {
        border-radius: 8px;
        background: #f6f8fa;
    }
}

.list_container_active {
    font-weight: 600;
    border-radius: 8px;
    background: rgba(246, 248, 250, 1);
    transition: 0.5s ease;
}

.ys-home-custom-text {
    font-family: Involve, sans-serif;
    font-style: italic;
    color: rgba(58, 62, 89, 1);
    font-size: 39px;
    font-weight: 500;
    line-height: 45px;
    @media(max-width:768px){
        font-size: 21px;
        line-height: 30px;
    }
}
</style>